import { config, router } from "@inertiajs/vue3";
import { nextTick } from "vue";
import { isNative } from "@/Helpers/capacitor.js";

const canDetectBrowserTransition =
    "hasUAVisualTransition" in PopStateEvent.prototype;
const isIosBrowser =
    !isNative() && /iPhone|iPad|iPod/.test(navigator.userAgent);

let currentPath = window.location.pathname;
let currentIndex = window.navigation?.currentEntry?.index;
let pendingPopstate = null;
let replayingPopstate = false;

const isEnabled = () =>
    typeof document.startViewTransition === "function" &&
    !window.matchMedia("(min-width: 48rem), (prefers-reduced-motion: reduce)")
        .matches;

const release = (transition, selector) => {
    const named = document.querySelectorAll(selector);

    named.forEach((el) => (el.style.viewTransitionName = "none"));
    transition.finished.finally(() =>
        named.forEach((el) => el.style.removeProperty("view-transition-name"))
    );
};

const releaseCoveredElements = (transition) => {
    const header = document.querySelector(".page-header");

    if (!header) return;

    if (header.querySelector("[id^='headlessui-popover-panel']")) {
        release(transition, ".page-header, .page-tabbar");
        return;
    }

    if (header.getBoundingClientRect().bottom <= 0) {
        release(transition, ".page-header");
    }
};

const withType = (type) => (transition) => {
    if (window.location.pathname === currentPath) {
        transition.skipTransition();
        return;
    }

    transition.types?.add(type);
    releaseCoveredElements(transition);
};

export const pageTransition = (type) => (isEnabled() ? withType(type) : false);

export const visitTransition = (type) => (visit) => {
    visit.viewTransition = type && pageTransition(type);
};

export const transitionView = (type, update) => {
    if (!isEnabled()) {
        update();
        return;
    }

    const transition = document.startViewTransition(() => {
        update();
        return nextTick();
    });
    transition.ready.catch(() => {});
    transition.types?.add(type);
    release(transition, ".page-header, .page-tabbar");
};

const visitOptions = (href, options) => {
    const method = (options.method ?? "get").toLowerCase();
    const isPartial = options.only?.length || options.except?.length;
    const path = new URL(href, window.location.href).pathname;

    if (
        options.viewTransition ||
        options.prefetch ||
        method !== "get" ||
        isPartial ||
        options.preserveState ||
        path === window.location.pathname
    ) {
        return {};
    }

    return { viewTransition: pageTransition("push") };
};

const shouldAnimatePopstate = (event) => {
    if (replayingPopstate || !event.state?.page || !isEnabled()) {
        return false;
    }

    if (event.hasUAVisualTransition) {
        return false;
    }

    if (!canDetectBrowserTransition && isIosBrowser) {
        return false;
    }

    return window.location.pathname !== currentPath;
};

const waitForNavigate = () =>
    new Promise((resolve) => {
        const done = () => {
            stop();
            clearTimeout(timeout);
            resolve();
        };
        const stop = router.on("navigate", done);
        const timeout = setTimeout(done, 1000);
    });

const popstateType = () => {
    if (window.navigation?.currentEntry?.index > currentIndex) {
        return "push";
    }

    return "pop";
};

const animatePopstate = (event) => {
    if (pendingPopstate) {
        event.stopImmediatePropagation();
        pendingPopstate = event;
        return;
    }

    if (!shouldAnimatePopstate(event)) return;

    event.stopImmediatePropagation();
    pendingPopstate = event;

    const transition = document.startViewTransition(() => {
        const { state } = pendingPopstate;
        const navigated = waitForNavigate();
        pendingPopstate = null;
        replayingPopstate = true;
        window.dispatchEvent(new PopStateEvent("popstate", { state }));
        replayingPopstate = false;
        return navigated;
    });
    transition.ready.catch(() => {});
    withType(popstateType())(transition);
};

const trackCurrentPage = (event) => {
    currentPath = new URL(event.detail.page.url, window.location.origin)
        .pathname;
    currentIndex = window.navigation?.currentEntry?.index;
};

export const initPageTransitions = () => {
    config.set("visitOptions", visitOptions);
    window.addEventListener("popstate", animatePopstate, true);
    router.on("navigate", trackCurrentPage);
    router.on("success", trackCurrentPage);
};
